import React, { useEffect, useMemo, useState } from 'react';

const MONTH_PAGE_SIZE = 40;

export const parseTransactionDate = (tx) => {
  if (tx?.createdAt instanceof Date && !Number.isNaN(tx.createdAt.getTime())) {
    return tx.createdAt;
  }
  if (tx?.date) {
    const direct = new Date(tx.date);
    if (!Number.isNaN(direct.getTime())) return direct;
    const match = String(tx.date).match(/(\d{1,2})\/(\d{1,2})\/(\d{4})/);
    if (match) {
      return new Date(Number(match[3]), Number(match[2]) - 1, Number(match[1]));
    }
  }
  return new Date(0);
};

export const groupTransactionsByMonthDay = (transactions) => {
  const monthMap = new Map();
  transactions.forEach((tx) => {
    const d = parseTransactionDate(tx);
    const monthKey = d.toLocaleDateString('en-IN', { month: 'long', year: 'numeric' });
    const dayKey = d.toLocaleDateString('en-IN', { weekday: 'long', day: '2-digit', month: 'short' });
    const sortTime = tx.sortTime ?? d.getTime();
    if (!monthMap.has(monthKey)) {
      monthMap.set(monthKey, { sortTime, days: new Map() });
    }
    const monthEntry = monthMap.get(monthKey);
    monthEntry.sortTime = Math.max(monthEntry.sortTime, sortTime);
    if (!monthEntry.days.has(dayKey)) {
      monthEntry.days.set(dayKey, { sortTime, transactions: [] });
    }
    const dayEntry = monthEntry.days.get(dayKey);
    dayEntry.sortTime = Math.max(dayEntry.sortTime, sortTime);
    dayEntry.transactions.push(tx);
  });

  return Array.from(monthMap.entries())
    .sort((a, b) => b[1].sortTime - a[1].sortTime)
    .map(([month, { days }]) => ({
      month,
      dayGroups: Array.from(days.entries())
        .sort((a, b) => b[1].sortTime - a[1].sortTime)
        .map(([day, { transactions: dayTxns }]) => ({
          day,
          transactions: dayTxns.sort((a, b) => (b.sortTime ?? 0) - (a.sortTime ?? 0)),
        })),
    }));
};

const sumByType = (txns, type) => txns
  .filter((tx) => tx.type === type)
  .reduce((sum, tx) => sum + Number(tx.amount || 0), 0);

const loadMoreBtnStyle = {
  display: 'block',
  width: '100%',
  textAlign: 'center',
  padding: '12px 0',
  marginTop: 4,
  background: 'transparent',
  border: 'none',
  borderTop: '1px solid rgba(255,255,255,0.06)',
  color: '#8896a8',
  fontSize: 12,
  cursor: 'pointer',
  fontFamily: 'inherit',
};

const TransactionRow = ({ tx, t }) => {
  const isCredit = tx.type === 'credit';
  const parsed = parseTransactionDate(tx);
  const hasDate = parsed instanceof Date && parsed.getTime() > 0;
  const dateLabel = hasDate
    ? parsed.toLocaleDateString('en-IN', { day: '2-digit', month: 'short' })
    : '';
  const timeLabel = tx.timeLabel
    || (hasDate
      ? parsed.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })
      : '');
  const na = t('naLabel') || 'N/A';
  const showRef = tx.ref && tx.ref !== na && tx.ref !== 'N/A';
  const refShort = showRef ? String(tx.ref).slice(0, 12) : '';
  const subtitle = [dateLabel, timeLabel].filter(Boolean).join(' · ')
    + (refShort ? ` · ${refShort}` : '');

  return (
    <div style={{
      display: 'flex',
      alignItems: 'center',
      padding: '12px 0',
      borderBottom: '1px solid rgba(255,255,255,0.04)',
    }}
    >
      <div style={{
        width: 36,
        height: 36,
        borderRadius: '50%',
        flexShrink: 0,
        background: isCredit ? 'rgba(76,175,80,0.12)' : 'rgba(233,69,96,0.12)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: 14,
        fontWeight: 700,
        marginRight: 12,
        color: isCredit ? '#4CAF50' : '#e94560',
      }}
      >
        {isCredit ? '↑' : '↓'}
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{
          fontSize: 13,
          color: '#e8e0d0',
          fontWeight: 500,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
        >
          {tx.desc}
        </div>
        <div style={{ fontSize: 11, color: '#8896a8', marginTop: 2 }}>
          {subtitle}
        </div>
      </div>
      <div style={{ textAlign: 'right', flexShrink: 0, marginLeft: 12 }}>
        <div style={{
          fontSize: 14,
          fontWeight: 700,
          color: isCredit ? '#4CAF50' : '#e94560',
        }}
        >
          {isCredit ? '+' : '-'}₹{Math.abs(Number(tx.amount) || 0).toLocaleString('en-IN')}
        </div>
        {tx.status && tx.status !== 'captured' && (
          <div style={{ fontSize: 10, color: '#FF9800', marginTop: 2 }}>{tx.status}</div>
        )}
      </div>
    </div>
  );
};

const TransactionMonthAccordion = ({ month, dayGroups, t, open, onToggle }) => {
  const [visibleCount, setVisibleCount] = useState(MONTH_PAGE_SIZE);
  const monthTxns = useMemo(() => dayGroups.flatMap((g) => g.transactions), [dayGroups]);
  const credits = sumByType(monthTxns, 'credit');
  const debits = sumByType(monthTxns, 'debit');
  const monthNet = credits - debits;

  useEffect(() => {
    setVisibleCount(MONTH_PAGE_SIZE);
  }, [month, monthTxns.length]);

  const visibleTxns = open ? monthTxns.slice(0, visibleCount) : [];
  const visibleByDay = [];
  if (open) {
    const remaining = new Set(visibleTxns);
    dayGroups.forEach(({ day, transactions }) => {
      const shown = transactions.filter((tx) => remaining.has(tx));
      if (shown.length > 0) visibleByDay.push({ day, transactions: shown });
    });
  }

  return (
    <div>
      <button
        type="button"
        onClick={onToggle}
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '8px 0',
          marginBottom: 8,
          cursor: 'pointer',
          width: '100%',
          background: 'none',
          border: 'none',
          borderBottom: '1px solid rgba(255,255,255,0.06)',
          fontFamily: 'inherit',
          textAlign: 'left',
        }}
      >
        <div>
          <span style={{ fontSize: 13, fontWeight: 600, color: '#e8e0d0' }}>{month}</span>
          <span style={{ fontSize: 11, color: '#8896a8', marginLeft: 8 }}>
            {monthTxns.length}
          </span>
        </div>
        <div style={{ textAlign: 'right' }}>
          <div style={{
            fontSize: 13,
            fontWeight: 600,
            color: monthNet >= 0 ? '#4CAF50' : '#e94560',
          }}
          >
            {monthNet >= 0 ? '+' : '-'}₹{Math.abs(monthNet).toLocaleString('en-IN')}
          </div>
          <div style={{ fontSize: 10, color: '#8896a8' }}>net</div>
        </div>
      </button>
      {open && (
        <div>
          {visibleByDay.map(({ day, transactions }) => (
            <div key={day}>
              <div style={{
                fontSize: 11,
                color: '#8896a8',
                padding: '10px 0 2px',
                letterSpacing: '0.04em',
              }}
              >
                {day}
              </div>
              {transactions.map((tx, i) => (
                <TransactionRow key={tx.id || tx.ref || `${day}-${i}`} tx={tx} t={t} />
              ))}
            </div>
          ))}
          {monthTxns.length > visibleCount && (
            <button
              type="button"
              onClick={() => setVisibleCount((n) => n + MONTH_PAGE_SIZE)}
              style={loadMoreBtnStyle}
            >
              Load more transactions ↓
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default function ClientTransactionHistory({
  transactions,
  t,
  compact = false,
  filterControls = null,
  hasMore = false,
  loadingMore = false,
  onLoadMore = null,
}) {
  const grouped = useMemo(() => groupTransactionsByMonthDay(transactions), [transactions]);
  const [openMonth, setOpenMonth] = useState(null);

  useEffect(() => {
    setOpenMonth((prev) => {
      if (prev && grouped.some((g) => g.month === prev)) return prev;
      return grouped[0]?.month || null;
    });
  }, [grouped]);

  return (
    <>
      {filterControls}
      <div
        className="client-txn-history-scroll -webkit-overflow-scrolling-touch overflow-y-auto overflow-x-hidden pr-0.5"
        style={{ maxHeight: compact ? 'min(58vh, 520px)' : 'min(68vh, 640px)' }}
      >
        {grouped.map(({ month, dayGroups }) => (
          <TransactionMonthAccordion
            key={month}
            month={month}
            dayGroups={dayGroups}
            t={t}
            open={openMonth === month}
            onToggle={() => setOpenMonth((prev) => (prev === month ? null : month))}
          />
        ))}
        {hasMore && typeof onLoadMore === 'function' && (
          <button
            type="button"
            onClick={onLoadMore}
            disabled={loadingMore}
            style={{ ...loadMoreBtnStyle, opacity: loadingMore ? 0.7 : 1, cursor: loadingMore ? 'default' : 'pointer' }}
          >
            {loadingMore ? t('loading') : 'Load more transactions ↓'}
          </button>
        )}
      </div>
      {transactions.length === 0 && (
        <p className="m-0 py-3 text-center text-[11px] text-de-muted">{t('noTransactionsFilter')}</p>
      )}
    </>
  );
}
